import React from "react";

import { SEND_DAYS, STAGES, channelLabel, dateAfter } from "../../campaignUtils";
import { Action, Alert, Card, Chip, HelpTip, Spinner } from "../../shared/StudioControls";
import { JobProgress, JobSteps, getJobSteps } from "../../shared/JobSteps";
import { getSelectedLanguages, hasCompleteContent } from "../campaignDetails.utils";
import { ContentEditor } from "./ContentEditor";
import { ContentPreview } from "./ContentPreview";
import { VariantTabs } from "./VariantTabs";

export const ContentSmsErrors = ({ errors }) => errors.length > 0 && (
  <Alert tone="danger" title="SMS sending is blocked">
    <p>Shorten these messages to 160 characters or fewer:</p>
    <ul>{errors.map(error => <li key={error}>{error}</li>)}</ul>
  </Alert>
);

export const ContentStep = ({ model, derived, handlers }) => {
  const { campaign, configuration } = derived;
  const payload = campaign.payload;
  const hasContent = hasCompleteContent(payload) && model.content.length === STAGES.length;
  const generating = campaign.status === "generating";
  const stageIndex = STAGES.indexOf(model.stage);
  const variantAt = (stageName, channel, language) =>
    ((((model.content[STAGES.indexOf(stageName)] || {}).variants || {})[channel] || {})[language]);
  const variant = variantAt(model.stage, model.channel, model.language);
  // Messages appear in the editor one by one as the background job saves them.
  const hasWritten = generating && model.content.some(stage => stage && stage.variants && Object.keys(stage.variants).length > 0);
  const liveTabs = generating && payload.settings
    ? { channels: payload.settings.channels, languages: getSelectedLanguages(payload.settings), isWriting: (...args) => !variantAt(...args) }
    : {};

  return (
    <Card
      labelledBy="cs-step-content"
      title="Notification content"
      help={["Personalized automatically for the scheduled channel", "English and Sinhala follow the customer’s preferred language"]}
      aside={<Chip className="cs-chip--ai">Generated automatically</Chip>}
    >
      {payload.sample_preview && (
        <Alert tone="info" title="Sample preview data">
          These four English and Sinhala send-day messages demonstrate the weekly experience. They are not scheduled or sent to customers.
        </Alert>
      )}
      {generating && <JobProgress progress={payload.progress} fallbackTitle="Preparing personalized notifications…" />}

      {!configuration.ai_ready && (
        <Alert tone="warning" title="AI generation isn’t configured">
          {configuration.message || "Ask the backend administrator to configure the AI key and business scope."}
        </Alert>
      )}

      {model.stale && (
        <Alert tone="warning" title="A newer revision was saved by another admin">
          <ul>
            <li>Your draft is kept so you can copy from it</li>
            <li>Discard it to load the latest revision before saving</li>
          </ul>
        </Alert>
      )}

      {payload.error && campaign.status !== "generating" && (
        <Alert tone="danger" title="Notification preparation stopped">
          <p>{payload.error}</p>
          <JobSteps steps={getJobSteps(payload.progress)} />
          {payload.generation_id && (
            <React.Fragment>
              <Action disabled={!derived.canRetryGeneration} onClick={handlers.onRetryGeneration}>Retry failed step and continue</Action>
              {model.dirty && <p className="cs-muted">Discard unsaved edits before retrying the saved generation.</p>}
            </React.Fragment>
          )}
        </Alert>
      )}

      {!generating && !payload.error && !hasContent && (
        <Alert tone="warning" title="The weekly package is incomplete">
           This notification set does not contain all four send-day messages in English and Sinhala. It is not available for preview or delivery until generation completes.
        </Alert>
      )}

      {(hasContent || hasWritten) && (
        <div className="cs-section" aria-live={generating ? "polite" : undefined}>
          <h3 className="cs-subheading">
            {generating ? "Preview notifications as they are prepared" : "Notification preview"}
            <HelpTip
              label="About editing"
              points={[
                "This notification set sends on Days 1, 3, 5 and 7; Days 2, 4 and 6 are rest days",
                generating ? "The preview updates as each notification becomes ready" : "This is the saved notification content"
              ]}
            />
          </h3>
          <VariantTabs
            content={model.content}
            stage={model.stage}
            channel={model.channel}
            language={model.language}
            {...liveTabs}
            onStageChange={handlers.onStageChange}
            onChannelChange={handlers.onChannelChange}
            onLanguageChange={handlers.onLanguageChange}
          />
          {variant ? (
            <div className="cs-editor" role="tabpanel" aria-label={`${model.stage} ${model.channel} ${model.language}`}>
              <ContentEditor variant={variant} channel={model.channel} isEditable={false} onEdit={handlers.onEdit} />
              <ContentPreview
                variant={variant}
                channel={model.channel}
                stage={model.stage}
                stageIndex={stageIndex}
                language={model.language}
                banners={payload.banners}
                preview={model.preview}
                lead={model.lead}
                audience={model.audience}
                canUseSaved={derived.canUseSaved}
                isGenerating={generating}
                isBusy={model.busy}
                onLeadChange={handlers.onLeadChange}
                onPreview={handlers.onPreview}
                previewDate={campaign.cycle ? dateAfter(campaign.cycle.start_date, SEND_DAYS[stageIndex] - 1) : undefined}
              />
            </div>
          ) : generating ? (
            <div className="cs-progress-panel" role="tabpanel" aria-label={`${model.stage} ${model.channel} ${model.language}`}>
              <Spinner label={`AI is still writing ${model.stage} · ${channelLabel(model.channel)} · ${model.language}. It appears here when ready.`} />
            </div>
          ) : (
            <p className="cs-muted">This language isn’t part of the saved package. Choose another tab.</p>
          )}
          <ContentSmsErrors errors={derived.smsErrors} />
        </div>
      )}
    </Card>
  );
};
